import { type ChangeEvent, useCallback, useEffect, useState } from 'react';

import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { useStoreApi as useMotionEditorStoreApi } from '../motionEditorStore';

const MOTION_DESCRIPTION_PLACEHOLDER =
  'Ex. Confident walk with a slight bounce, arms swinging naturally';

interface MotionDescriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function MotionDescriptionModal({
  open,
  onOpenChange,
}: MotionDescriptionModalProps) {
  const motionEditorStoreApi = useMotionEditorStoreApi();
  const [draftDescription, setDraftDescription] = useState('');

  useEffect(() => {
    if (open) {
      setDraftDescription(motionEditorStoreApi.state.motionDescription);
    }
  }, [open, motionEditorStoreApi]);

  const handleSave = useCallback(() => {
    motionEditorStoreApi.setMotionDescription(draftDescription.trim());
    onOpenChange(false);
  }, [draftDescription, motionEditorStoreApi, onOpenChange]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Modal
      title="Motion description"
      description="Describe the motion you want to create. This prompt is additive—it helps the editor generate better in-betweens among your key poses."
      maxWidth={480}
      open={open}
      onOpenChange={onOpenChange}
      footer={
        <div className="flex justify-between">
          <Button type="button" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleSave}>
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-2">
        <Text variant="studio/body/medium/sm" className="text-secondary">
          Motion prompt
        </Text>
        <Input
          as="textarea"
          autoResize
          name="motion-description"
          placeholder={MOTION_DESCRIPTION_PLACEHOLDER}
          value={draftDescription}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            setDraftDescription(event.target.value);
          }}
        />
      </div>
    </Modal>
  );
}
